import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { AudienceService } from 'app/library/audience/services/audience.service';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { BehaviorSubject, Observable, race } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    CreateValidScheduledStreamContent,
    CreateValidScheduledStreamContentBulk,
    CreateValidScheduledStreamContentBulkSuccess,
    CreateValidScheduledStreamContentSuccess,
    DeleteScheduledStreamContent,
    DeleteScheduledStreamContentFailure,
    DeleteScheduledStreamContentSuccess,
    FetchScheduledStreamContents,
    FetchScheduledStreamContentsFailure,
    FetchScheduledStreamContentsSuccess,
    UpdateScheduledStreamContent,
    UpdateScheduledStreamContentFailure,
    UpdateScheduledStreamContentSuccess,
} from '../actions/index';
import { IScheduleStreamContentActionPayload } from '../interfaces/schedule-stream-content.action-payload.interface';
import { MaxBrainScheduledStreamContent } from '../models/scheduled-stream-content.model';
import { ScheduledStreamContentState } from '../models/scheduled-stream-content.state';
import { ScheduledStreamContentsState } from '../models/scheduled-stream-contents.state';
import { IScheduleStreamContentBulkActionPayload } from '../interfaces/schedule-stream-content-bulk.action-payload.interface';

@Injectable()
export class ScheduledStreamContentService {
    @Select(ScheduledStreamContentsState.getFilteredEntities)
    scheduledStreamContent$: Observable<MaxBrainScheduledStreamContent[]>;

    @Select(ScheduledStreamContentsState.getFilteredEntities)
    filteredScheduledStreamContent$: Observable<MaxBrainScheduledStreamContent[]>;

    private _newScheduledStreamContentSubject = new BehaviorSubject<MaxBrainScheduledStreamContent>(null);
    public newScheduledStreamContentSubject$ = this._newScheduledStreamContentSubject.asObservable();

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper, private _audienceService: AudienceService) {}

    getScheduledStreamContents(): MaxBrainScheduledStreamContent[] {
        return this._store.selectSnapshot(ScheduledStreamContentsState.getFilteredEntities);
    }

    getScheduledStreamContentsById(id: string): MaxBrainScheduledStreamContent {
        return ScheduledStreamContentState.getEntity(this._store.selectSnapshot(ScheduledStreamContentState), id);
    }

    async fetchScheduledStreamContents(audienceId: string, featuredStreamContentId: string, pageSize: number, pageNumber: number): Promise<boolean> {
        this._store.dispatch(new FetchScheduledStreamContents({ audienceId, featuredStreamContentId, pageSize, pageNumber }));
        return await this._actionHelper.race(FetchScheduledStreamContentsSuccess, FetchScheduledStreamContentsFailure).toPromise();
    }

    createScheduledStreamContent(scheduledStreamContent: IScheduleStreamContentActionPayload) {
        this._store.dispatch(new CreateValidScheduledStreamContent(scheduledStreamContent));

        this._action$.pipe(ofActionDispatched(CreateValidScheduledStreamContentSuccess), take(1)).subscribe((action: CreateValidScheduledStreamContentSuccess) => {
            this._newScheduledStreamContentSubject.next(action.payload);
            this._audienceService.fetchAudiences();
        });
    }

    createScheduledStreamContentBulk(scheduledStreamContent: IScheduleStreamContentBulkActionPayload) {
        this._store.dispatch(new CreateValidScheduledStreamContentBulk(scheduledStreamContent));

        this._action$.pipe(ofActionDispatched(CreateValidScheduledStreamContentBulkSuccess), take(1)).subscribe((action: CreateValidScheduledStreamContentBulkSuccess) => {
            this._audienceService.fetchAudiences();
        });
    }

    async updateScheduledStreamContent(scheduledStreamContent: MaxBrainScheduledStreamContent): Promise<boolean> {
        this._store.dispatch(new UpdateScheduledStreamContent(scheduledStreamContent));
        return await this._actionHelper.race(UpdateScheduledStreamContentSuccess, UpdateScheduledStreamContentFailure).toPromise();
    }

    async deleteScheduledStreamContent(scheduledStreamContentId: string): Promise<boolean> {
        this._store.dispatch(new DeleteScheduledStreamContent(scheduledStreamContentId));

        return await this._actionHelper.race(DeleteScheduledStreamContentSuccess, DeleteScheduledStreamContentFailure).toPromise();
    }
}
